/**
 * 収録の設定（/shoot/recording。Issue #791）。収録でどの TetherCam のデバイス（名乗った名前。設定は「TetherCam」のページ）を何に使うか:
 * - ライブ: P1 のデバイス / P2 のデバイス（座標はそのデバイスの設定のその席の枠）
 * - 音声認識の経路: 音を配るデバイス（既定は OBS を名乗るもの）と経路（ローカルの iPhone の待機へ直接 / 補正サーバーの中継 / 使わない）。
 *   どのトラックかは試合ごと（プレイヤー名と同じ名前のトラック）に、録画開始の直前（待機中）に補正サーバーが決める
 * - 録画とアップロードを有効にするデバイス（配信に使っていないデバイスも）
 * - フィールド生成: P1 のデバイス / P2 のデバイス
 * 保存は SetRecordingSetup。足りない機能（features）や座標は注意として出すだけ（保存は止めない）。
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/api';
import { usePageTitle } from '../appInfo';
import ShootNav from '../components/ShootNav';
import { mergeDevices, useLiveDevices, useSavedDevices } from './TetherCamDevices';

const SEATS = ['1', '2'];
const ROUTES = [
  ['local', 'ローカルの iPhone へ直接（P1 / P2 のデバイスの待機へ。既定）'],
  ['relay', '補正サーバーの中継を通す（iPhone が受信しに行く。会場の Wi-Fi で届かないとき）'],
  ['off', '使わない'],
];

export const EMPTY = { live: { 1: null, 2: null }, speech: { from: null, route: 'local' }, record: [], field_gen: { 1: null, 2: null } };

const has = (features, name) => !features || !!features?.[name]?.supported;

/** 既定の配り手: OBS を名乗るデバイス（platform obs かチャンネル obs） */
export function defaultSpeechFrom(rows) {
  const obs = rows.find((r) => r.live?.state?.device?.platform === 'obs' || r.live?.channel === 'obs');
  return obs?.name ?? null;
}

/** 注意（保存は止めない）。rows は mergeDevices の結果 */
export function setupWarnings(doc, rows) {
  const by = Object.fromEntries(rows.map((r) => [r.name, r]));
  const features = (n) => by[n]?.live?.state?.features ?? null;
  const entries = (n) => by[n]?.saved?.doc?.entries ?? [];
  const seatSlots = (n, seat) => entries(n).filter((e) => e.player_number === Number(seat));
  const out = [];
  SEATS.forEach((s) => {
    const n = doc.live?.[s];
    if (n && !seatSlots(n, s).some((e) => e.field_type === 'field')) out.push(`ライブの P${s}: ${n} に P${s} のフィールドの座標がありません（「TetherCam」で決める）`);
    if (n && !has(features(n), 'stream')) out.push(`ライブの P${s}: ${n} は配信に対応していません`);
    const g = doc.field_gen?.[s];
    if (g && !seatSlots(g, s).some((e) => e.field_type === 'field')) out.push(`フィールド生成の P${s}: ${g} に P${s} のフィールドの座標がありません`);
    if (g && !(doc.record ?? []).includes(g)) out.push(`フィールド生成の P${s}: ${g} は録画とアップロードが有効になっていません`);
  });
  const sp = doc.speech ?? {};
  if (sp.route !== 'off') {
    if (!sp.from) out.push('音声認識: 音を配るデバイスを選んでください');
    else if (!has(features(sp.from), 'audio_out')) out.push(`音声認識: ${sp.from} は音を流す機能（audio_out）に対応していません`);
    const need = sp.route === 'relay' ? 'speech_pull' : 'speech_listen';
    SEATS.forEach((s) => {
      const n = doc.live?.[s];
      if (n && n !== sp.from && !has(features(n), need)) out.push(`音声認識: P${s} の ${n} は${sp.route === 'relay' ? '受信' : '待機'}（${need}）に対応していません`);
    });
  }
  (doc.record ?? []).forEach((n) => {
    if (!has(features(n), 'record') || !has(features(n), 'upload')) out.push(`録画: ${n} は録画かアップロードに対応していません`);
  });
  return out;
}

function DeviceSelect({ value, names, onChange, testId }) {
  return (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} data-testid={testId}>
      <option value="">（なし）</option>
      {names.map((n) => <option key={n} value={n}>{n}</option>)}
      {value && !names.includes(value) ? <option value={value}>{value}（見当たらない）</option> : null}
    </select>
  );
}

export default function RecordingSetup({ fetchImpl = null }) {
  usePageTitle('収録の設定');
  const live = useLiveDevices(fetchImpl);
  const [saved] = useSavedDevices();
  const rows = useMemo(() => mergeDevices(live, saved), [live, saved]);
  const names = rows.map((r) => r.name);
  const [doc, setDoc] = useState(undefined);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.resolve(api.project('GetRecordingSetup', {})).catch(() => ({ doc: null })).then((r) => {
      if (alive) setDoc(r?.doc ?? null);
    });
    return () => { alive = false; };
  }, []);

  // 保存が無ければ、配り手は OBS を名乗るデバイスを既定に（見つかったら 1 回だけ）
  const cur = doc ?? EMPTY;
  useEffect(() => {
    if (doc === null && !cur.speech.from) {
      const obs = defaultSpeechFrom(rows);
      if (obs) setDoc({ ...EMPTY, speech: { ...EMPTY.speech, from: obs } });
    }
  }, [doc, rows, cur.speech.from]);

  const set = (patch) => { setDoc({ ...cur, ...patch }); setDirty(true); setMsg(null); };
  const save = async () => {
    try {
      const r = await api.project('SetRecordingSetup', { doc: cur });
      setDoc(r?.doc ?? cur);
      setDirty(false);
      setMsg('保存しました（補正サーバーが数秒で読みます）');
    } catch (e) {
      setMsg(`保存できませんでした: ${e?.message ?? e}`);
    }
  };
  const warnings = setupWarnings(cur, rows);
  const toggleRecord = (n) => {
    const rec = new Set(cur.record ?? []);
    if (rec.has(n)) rec.delete(n); else rec.add(n);
    set({ record: [...rec].sort() });
  };

  if (doc === undefined) return <><ShootNav /><div className="page">読み込み中…</div></>;
  const connected = (n) => rows.find((r) => r.name === n)?.live?.connected;
  return (
    <>
    <ShootNav />
    <div className="page" data-testid="recording-setup">
      <div className="page-header"><h2>収録の設定</h2></div>
      <p style={{ fontSize: 14, lineHeight: 1.7, margin: 0 }}>
        収録でどの TetherCam のデバイスを使うかを決めます。デバイスの座標と音声認識の設定は「TetherCam」のページで、デバイスごとに決めます。
      </p>
      <section className="card" data-testid="rec-live" style={{ display: 'grid', gap: 8, fontSize: 13 }}>
        <h3 className="card-title" style={{ margin: 0 }}>ライブ（補正サーバーの合成）</h3>
        {SEATS.map((s) => (
          <label key={s}>P{s} のデバイス{' '}
            <DeviceSelect value={cur.live?.[s]} names={names} testId={`live-${s}`} onChange={(v) => set({ live: { ...cur.live, [s]: v } })} />
          </label>
        ))}
        <div style={{ color: '#8a93a8' }}>座標はそのデバイスの設定のその席の枠（P1 なら P1 のフィールド・手札・認識領域）を使います。同じデバイスを両方に選べます（1 枚に両席が映るとき）。</div>
      </section>
      <section className="card" data-testid="rec-speech" style={{ display: 'grid', gap: 8, fontSize: 13 }}>
        <h3 className="card-title" style={{ margin: 0 }}>音声認識の経路</h3>
        <label>音を配るデバイス{' '}
          <DeviceSelect value={cur.speech?.from} names={names} testId="speech-from" onChange={(v) => set({ speech: { ...cur.speech, from: v } })} />
        </label>
        {ROUTES.map(([id, label]) => (
          <label key={id}><input type="radio" name="speech-route" checked={(cur.speech?.route ?? 'local') === id} data-testid={`route-${id}`}
            onChange={() => set({ speech: { ...cur.speech, route: id } })} /> {label}</label>
        ))}
        <div style={{ color: '#8a93a8' }}>どのトラックを流すかは試合ごとです。録画を始める直前（待機中）に、プレイヤー名と同じ名前のトラックを補正サーバーが選んで、その席のデバイスへ流させます。</div>
      </section>
      <section className="card" data-testid="rec-record" style={{ display: 'grid', gap: 6, fontSize: 13 }}>
        <h3 className="card-title" style={{ margin: 0 }}>録画とアップロード</h3>
        {!names.length ? <div style={{ color: '#8a93a8' }}>デバイスがまだありません</div> : null}
        {names.map((n) => (
          <label key={n}><input type="checkbox" checked={(cur.record ?? []).includes(n)} onChange={() => toggleRecord(n)} data-testid={`record-${n}`} />
            {' '}{n}{connected(n) ? '' : '（今は来ていない）'}</label>
        ))}
        <div style={{ color: '#8a93a8' }}>選んだデバイスだけに録画の開始・停止を伝え、録画を本番へ上げます。配信に使っていないデバイスも選べます。</div>
      </section>
      <section className="card" data-testid="rec-fieldgen" style={{ display: 'grid', gap: 8, fontSize: 13 }}>
        <h3 className="card-title" style={{ margin: 0 }}>フィールド生成</h3>
        {SEATS.map((s) => (
          <label key={s}>P{s} のデバイス{' '}
            <DeviceSelect value={cur.field_gen?.[s]} names={names} testId={`fieldgen-${s}`} onChange={(v) => set({ field_gen: { ...cur.field_gen, [s]: v } })} />
          </label>
        ))}
        <div style={{ color: '#8a93a8' }}>録画が上がったら、このデバイスの録画とその席の座標でフィールド動画を作ります。</div>
      </section>
      {warnings.length ? (
        <ul data-testid="rec-warnings" style={{ fontSize: 13, color: '#f0c36b', margin: 0 }}>
          {warnings.map((w) => <li key={w}>{w}</li>)}
        </ul>
      ) : null}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button" onClick={save} disabled={!dirty} data-testid="rec-save" style={{ padding: '6px 16px', fontWeight: 700 }}>保存</button>
        <span data-testid="rec-message" style={{ fontSize: 12, color: '#8a93a8' }}>{msg ?? (dirty ? '保存していない変更があります' : '')}</span>
      </div>
    </div>
    </>
  );
}

